import cloneDeep from 'lodash.clonedeep';
import { action, computed, makeObservable, observable } from 'mobx';
import {
  defaultPreparationProfile,
  defaultPreparationProfileValidation,
  preOrderPeriodMaxLength,
  PreOrderPeriodUnit,
} from '../constants/preparationProfiles';
import { getPreparationProfileParams, preparationProfileCast } from '../helpers/preparationProfilesHelpers';
import {
  IPartialPreparationProfile,
  IPreparationProfile,
  IPreparationProfileValidation,
  IResponsePreparationProfile,
  TPreparationProfileBranch,
} from '../types/preparationProfiles';
import client from 'src/services/client';
import { LoadingStatus } from 'src/types/main';
import { getAbortController, isCanceledRequest } from '~/helpers';
import logger from '~/services/logger';
import { scrollToElement, scrollToErrorElement } from '~/utils/helpers';
import { emptyNumberValidation } from '~/utils/validation';

class PreparationProfilesStore {
  constructor() {
    makeObservable(this);
  }

  private getProfilesListController = new AbortController();
  private getProfileController = new AbortController();
  private updateProfileController = new AbortController();

  @observable loadingList: LoadingStatus = LoadingStatus.Init;
  @observable loadingProfile: LoadingStatus = LoadingStatus.Init;
  @observable profiles: IPartialPreparationProfile[] = [];
  @observable selectedProfile: IPreparationProfile = defaultPreparationProfile;
  @observable isEdit: boolean = false;

  @computed get isLoading(): boolean {
    return (
      [LoadingStatus.Loading, LoadingStatus.Refreshing].includes(this.loadingList) ||
      [LoadingStatus.Loading, LoadingStatus.Refreshing].includes(this.loadingProfile)
    );
  }

  @computed get isSelectedDefaultProfile(): boolean {
    return Boolean(this.selectedProfile.is_default);
  }

  public init = () => {
    this.loadingList = LoadingStatus.Init;
    this.loadingProfile = LoadingStatus.Init;
    this.isEdit = false;

    this.getProfilesList();
  };

  private getProfilesList = async (refreshList?: boolean) => {
    this.getProfilesListController.abort();
    this.getProfilesListController = getAbortController();

    try {
      this.loadingList = LoadingStatus.Loading;

      const response: { data: IPartialPreparationProfile[] } = await client.get('v4/orders-settings/profiles', {
        signal: this.getProfilesListController.signal,
      });

      this.profiles = response?.data.map((profile) => profile) || [];

      if (!refreshList && this.profiles.length) {
        const isSelectProfileAvailable: boolean = this.profiles.some((profile) => profile.id === this.selectedProfile.id);

        this.getProfile(isSelectProfileAvailable ? this.selectedProfile.id : this.profiles[0].id);
      }

      this.loadingList = LoadingStatus.Success;
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.loadingList = LoadingStatus.Success;
        return;
      }

      this.loadingList = LoadingStatus.Error;
      logger.error({ error, place: 'preparationProfilesStore getProfilesList', showModal: true });
    }
  };

  @action getProfile = async (profileId: number) => {
    this.getProfileController.abort();
    this.getProfileController = getAbortController();

    try {
      this.loadingProfile = LoadingStatus.Loading;

      const response: { profile: IResponsePreparationProfile } = await client.get(`v4/orders-settings/profiles/${profileId}`, {
        signal: this.getProfileController.signal,
      });

      this.selectProfile(preparationProfileCast(response.profile));
      this.loadingProfile = LoadingStatus.Success;
    } catch (error: any) {
      this.loadingProfile = LoadingStatus.Error;
      logger.error({ error, place: 'preparationProfilesStore getProfile', showModal: true });
    }
  };

  @action private selectProfile = (profile: IPreparationProfile) => {
    this.selectedProfile = profile;
    this.isEdit = false;
    this.scrollToProfile(profile.id);

    // reset validation
    this.validation = cloneDeep(defaultPreparationProfileValidation);
  };
  private scrollToProfile = (id: number) => scrollToElement({ id: `profile_${id}`, scrollParams: { block: 'center' } });

  @action removeBranchFromProfile = (branchId: number) => {
    const branches: TPreparationProfileBranch[] = this.selectedProfile.branches.filter((branch) => branch.id !== branchId);
    this.changeSelectedProfile({ branches });
  };

  @action changeSelectedProfile = (data: Partial<IPreparationProfile>) => {
    this.selectedProfile = { ...this.selectedProfile, ...data };
    this.isEdit = true;

    if (data.name) {
      this.profiles = this.profiles.map((profile) => (profile.id === this.selectedProfile.id ? { ...profile, name: data.name! } : profile));
    }

    // update validation
    if (!this.validation.isValid) this.setValidation();
  };

  public submit = () => {
    this.setValidation();
    if (!this.validation.isValid) {
      scrollToErrorElement();
      return;
    }

    this.updateProfile();
  };

  @action createProfile = async () => {
    try {
      this.loadingList = LoadingStatus.Loading;

      const response: { profile: IResponsePreparationProfile } = await client.post('v4/orders-settings/profiles/create');

      const newProfile: IPreparationProfile = preparationProfileCast(response.profile);
      this.profiles.push(newProfile);
      this.selectProfile(newProfile);

      this.loadingList = LoadingStatus.Success;
    } catch (error: any) {
      this.loadingList = LoadingStatus.Error;
      logger.error({ error, place: 'preparationProfilesStore createProfile', showModal: true });
    }
  };

  @action updateProfile = async () => {
    this.updateProfileController.abort();
    this.updateProfileController = getAbortController();

    try {
      this.loadingProfile = LoadingStatus.Loading;
      const params = getPreparationProfileParams(this.selectedProfile);

      await client.put(`v4/orders-settings/profiles/${this.selectedProfile.id}`, params, { signal: this.updateProfileController.signal });

      this.getProfilesList(true);
      this.isEdit = false;
      this.loadingProfile = LoadingStatus.Success;
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.loadingProfile = LoadingStatus.Success;
        return;
      }

      this.loadingProfile = LoadingStatus.Error;
      logger.error({ error, place: 'preparationProfilesStore updateProfile', showModal: true });
    }
  };

  @action deleteProfile = async (id: number) => {
    try {
      this.loadingProfile = LoadingStatus.Loading;

      await client.delete(`v4/orders-settings/profiles/${id}`);

      this.getProfilesList();
      this.loadingProfile = LoadingStatus.Success;
    } catch (error: any) {
      this.loadingProfile = LoadingStatus.Error;
      logger.error({ error, place: 'preparationProfilesStore deleteProfile', showModal: true });
    }
  };

  @observable validation: IPreparationProfileValidation = cloneDeep(defaultPreparationProfileValidation);
  @action setValidation = () => {
    this.validation = cloneDeep(defaultPreparationProfileValidation);

    const { deliveryTime, deliveryPreparationTime, pickupTime, pickupPreparationTime, preOrderPeriod, preOrderPeriodUnit } = this.selectedProfile;

    // deliveryTime validation
    [this.validation.deliveryTime.isValid, this.validation.isValid, this.validation.deliveryTime.errorText] = emptyNumberValidation(
      deliveryTime,
      this.validation.deliveryTime.isValid,
      this.validation.isValid
    );
    // deliveryPreparationTime validation
    [this.validation.deliveryPreparationTime.isValid, this.validation.isValid, this.validation.deliveryPreparationTime.errorText] =
      emptyNumberValidation(deliveryPreparationTime, this.validation.deliveryPreparationTime.isValid, this.validation.isValid);

    // pickupTime validation
    [this.validation.pickupTime.isValid, this.validation.isValid, this.validation.pickupTime.errorText] = emptyNumberValidation(
      pickupTime,
      this.validation.pickupTime.isValid,
      this.validation.isValid
    );
    // pickupPreparationTime validation
    [this.validation.pickupPreparationTime.isValid, this.validation.isValid, this.validation.pickupPreparationTime.errorText] = emptyNumberValidation(
      pickupPreparationTime,
      this.validation.pickupPreparationTime.isValid,
      this.validation.isValid
    );

    // preOrderPeriodValueValidation
    if (preOrderPeriodUnit !== PreOrderPeriodUnit.ONLY_TODAY) {
      [this.validation.preOrderPeriod.isValid, this.validation.isValid] = emptyNumberValidation(
        preOrderPeriod,
        this.validation.preOrderPeriod.isValid,
        this.validation.isValid,
        true
      );

      if (Number(preOrderPeriod) > preOrderPeriodMaxLength[preOrderPeriodUnit]) {
        this.validation.preOrderPeriod.isValid = false;
        this.validation.isValid = false;
      }
    }
  };
}

const preparationProfilesStore = new PreparationProfilesStore();
export default preparationProfilesStore;
